<template>
	<div :id="'form-module-' + dModule.id" :style="formStyle">
		<form v-on:submit.prevent="submitForm">
			<div v-for="(item, i) in dModule.data.form.input" :key="i">
				<label
					class="form-label"
					:class="{
						required: item.required,
						'error-text': errors['input-' + item.id],
					}"
				>
					{{ item.label }}
				</label>

				<!-- Checkbox input -->
				<checkbox
					v-if="item.inputType == 'checkbox'"
					v-model="formValues['input-' + item.id]"
					class="mb-3"
					:style="{ color: dModule.layout.inputColor }"
					:item="item"
				/>

				<!-- Radio buttons input -->
				<radio-buttons
					v-else-if="item.inputType == 'radio'"
					v-model="formValues['input-' + item.id]"
					class="mb-3"
					:style="{ color: dModule.layout.inputColor }"
					:item="item"
				/>

				<!-- Phone -->
				<phone-input
					v-else-if="item.inputType == 'tel'"
					v-model="formValues['input-' + item.id]"
					class="mb-3"
					:class="{
						obfuscated:
							item.obfuscated && hidingItems['input-' + item.id],
						'input-error': errors['input-' + item.id],
					}"
					:item="item"
					:inputStyle="inputStyle"
					@object="(object) => (phoneObject[item.id] = object)"
				/>

				<!-- Date -->
				<date-picker
					v-else-if="item.inputType == 'date'"
					v-model="formValues['input-' + item.id]"
					:templateStyle="inputStyle"
					:templateRequired="!!item.required"
					:futureDates="!!item.allowFutureDates"
				/>

				<!-- File upload -->
				<file-upload
					v-else-if="item.inputType == 'file'"
					v-model="formValues['input-' + item.id]"
					class="mb-3"
					:item="item"
					:inputStyle="{ color: dModule.layout.inputColor }"
					@error="(error) => (uploadInputError = error)"
					:required="item.required == '1'"
				/>

				<!-- Select (dropdown) -->
				<select
					v-else-if="item.inputType == 'select'"
					v-model="formValues['input-' + item.id]"
					class="form-input form-input__regular mb-3"
					:style="inputStyle"
					:required="item.required == '1'"
				>
					<option :value="undefined" disabled selected>
						{{ $t("form.select-option") }}
					</option>

					<option
						v-for="option in item.options"
						:key="option.id"
						:value="option.value"
					>
						{{ option.name }}
					</option>
				</select>

				<!-- Email -->
				<input
					v-else-if="item.inputType == 'email'"
					v-model="formValues['input-' + item.id]"
					class="form-input form-input__regular mb-3"
					:class="{
						'input-error': errors['input-' + item.id],
						obfuscated:
							item.obfuscated && hidingItems['input-' + item.id],
					}"
					type="email"
					:required="item.required == '1'"
					:style="inputStyle"
				/>

				<!-- Regular Input (text & number) -->
				<input
					v-else
					v-model="formValues['input-' + item.id]"
					class="form-input form-input__regular"
					:class="{
						'mb-3':
							!item.restrict || !restrictions['input-' + item.id],
						obfuscated:
							item.obfuscated && hidingItems['input-' + item.id],
						'input-error': errors['input-' + item.id],
					}"
					:type="item.inputType"
					:required="item.required == '1'"
					:style="inputStyle"
				/>
				<span v-if="item.obfuscated" class="obfuscate-icon">
					<i
						class="fa"
						:class="{
							'fa-eye-slash': !hidingItems['input-' + item.id],
							'fa-eye': hidingItems['input-' + item.id],
						}"
						@click="toggleShow(item.id)"
					></i>
				</span>

				<!-- Input restriction error -->
				<p
					v-if="item.restrict && restrictions['input-' + item.id]"
					class="error-text mb-2"
				>
					{{ item.restrictError }}
				</p>

				<!-- Errors -->
				<div class="error-text mb-3" v-if="errors['input-' + item.id]">
					{{ errors["input-" + item.id] }}
				</div>
			</div>

			<!-- Terms and conditions -->
			<div v-if="dModule.data.form.terms" class="mb-3">
				<input type="checkbox" v-model="formValues['terms']" required />
				<label
					class="pl-2 cursor-pointer"
					:style="{ color: dModule.layout.inputColor }"
					@click="openTerms"
				>
					{{ dModule.data.form.termsLabel }}
				</label>
			</div>

			<!-- Phone error -->
			<p class="error-text mb-1" v-if="phoneInputError">
				<font-awesome-icon
					class="mr-1"
					:icon="['fas', 'exclamation-triangle']"
				/>
				{{ phoneInputError }}
			</p>

			<!-- Upload error -->
			<p class="error-text mb-1" v-if="uploadInputError">
				<font-awesome-icon
					class="mr-1"
					:icon="['fas', 'exclamation-triangle']"
				/>
				{{ uploadInputError }}
			</p>

			<!-- Submit button -->
			<div class="submit" :class="dModule.data.button.alignment">
				<button
					type="submit"
					class="submit__btn"
					:style="buttonStyle"
					:disabled="$store.getters.expired"
				>
					<font-awesome-icon
						v-if="submitting"
						class="spinner"
						:icon="['fas', 'spinner']"
					/>

					<div v-else>
						{{ dModule.data.button.label }}
					</div>
				</button>
			</div>
		</form>

		<!-- Error submit dialogs -->
		<error-dialog
			ref="errorDialog"
			:text="$t('after-submit.error.error')"
		/>
		<error-dialog
			ref="duplicateDialog"
			:text="dModule.data.action.duplicateError"
		/>
		<error-dialog
			ref="invalidCodeDialog"
			:text="
				dModule.data.action.codeError != 'Invalid code'
					? dModule.data.action.codeError
					: $t('form.error.invalid-code')
			"
		/>
		<error-dialog
			ref="missingRequiredDialog"
			:text="$t('after-submit.error.missing-required-field')"
		/>
		<error-dialog
			ref="invalidFieldDataDialog"
			:text="$t('after-submit.error.invalid-field-data')"
		/>
		<error-dialog
			ref="requiredTermsAndConditionsDialog"
			:text="$t('after-submit.error.required-terms-and-conditions')"
		/>

		<error-dialog
			ref="customer26692WrongAnswerErrorDialog"
			:text="customer26692WrongAnswerText"
		/>
		<error-dialog
			ref="customer26692duplicateDialogDialog"
			:text="customer26692DuplicateText"
		/>

		<!-- Loyalty verification dialogs -->
		<verification-code-dialog
			ref="verificationDialog"
			@code-submit="onCodeSubmitted"
		/>

		<!-- Success submit dialogs -->
		<popup-dialog
			ref="popupDialog"
			:popup="dModule.data.popup"
			:sendType="dModule.data.action.sendType"
			@close="onSuccessfulSumbitDialogClosed"
		/>
	</div>
</template>

<script>
import Vue from "vue";
import { EventBus } from "@/event-bus.js";
import { isMobile } from "mobile-device-detect";

import ErrorDialog from "@/components/dialogs/ErrorDialog.vue";
import PopupDialog from "@/components/dialogs/PopupDialog.vue";
import VerificationCodeDialog from "@/components/dialogs/VerificationCodeDialog.vue";

// Inputs
import PhoneInput from "@/components/tools/PhoneInput.vue";
import DatePicker from "@/components/tools/DatePicker.vue";
import Checkbox from "@/components/tools/Checkbox.vue";
import RadioButtons from "@/components/tools/RadioButtons.vue";
import FileUpload from "@/components/tools/FileUpload.vue";

export default {
	name: "FormModule",
	props: {
		data: Object,
	},
	components: {
		PopupDialog,
		ErrorDialog,
		PhoneInput,
		DatePicker,
		Checkbox,
		RadioButtons,
		FileUpload,
		VerificationCodeDialog,
	},
	data() {
		return {
			dModule: this.data,
			formValues: {},
			submitting: false,
			submitted: false,
			target: null,

			hidingItems: [],

			// Restriction (min-max for numbers)
			restrictions: [],

			phoneObject: {},
			phoneInputError: null,
			uploadInputError: null,

			errors: {
				// "input-18543": "This is an error message",
			},
		};
	},
	mounted() {
		if (this.$store.getters.settings.fullscreen) {
			document.forms[0].elements[0].focus();
		}

		let inputs = this.dModule.data.form.input;
		inputs.forEach((input) => {
			if (input.restrict) this.restrictions["input-" + input.id] = false;
			if (input.obfuscated)
				this.$set(this.hidingItems, "input-" + input.id, true);
			// Only number fields can have a restrict setting that keeps the value between a minimum and maximum
			if (input.inputType == "number" && input.restrict)
				this.restrictions["input-" + input.id] = false;
		});

		// Take query parameters and fill inputs
		this.prefillInputs();
	},
	methods: {
		onCodeSubmitted(code) {
			this.formValues.loyalty_verification_code = code;
			this.submitForm();
		},
		async submitForm() {
			// If already submitted, show success dialog
			if (this.submitted) {
				let sendType = this.dModule.data.action.sendType;

				// Retrigger wallet open
				if (sendType == "wallet") this.openWallet();

				return this.$refs.popupDialog.open();
			}

			this.submitting = true;

			// Check input restrictions
			if (!this.meetsRestrictionRequirements()) return;

			// Check if valid phone input
			if (!this.isValidPhone()) return;

			// Wait until recaptcha has been loaded.
			if (this.$store.getters.settings.googleRecaptcha) {
				await this.$recaptchaLoaded();

				const token = await this.$recaptcha("login");

				this.$store
					.dispatch("verify", token)
					.then(() => {
						this.submit();
					})
					.catch(() => {
						this.$refs.errorDialog.open();
						this.submitting = false;
					});
			} else {
				this.submit();
			}
		},
		submit() {
			this.formValues.module_id = this.dModule.id;

			this.$store
				.dispatch("submit", this.formValues)
				.then((resp) => {
					if (resp.status == "valid") {
						this.valid(resp);
					} else if (resp.status == "verification_required") {
						this.$refs.verificationDialog.open(resp.receiver);
					} else {
						this.invalid(resp);
					}
				})
				.catch(() => {
					this.$refs.errorDialog.open();
					this.submitting = false;
				});
		},
		valid(resp) {
			this.submitted = true;

			// Reload the page after submittion
			if (this.dModule.data.action.refreshtimerEnabled) {
				let charAmount = this.dModule.data.popup.text.length;
				let timeInSeconds = charAmount / 12.75;
				if (timeInSeconds < 5) timeInSeconds = 5;

				setTimeout(function () {
					window.location.reload();
				}, Math.round(timeInSeconds) * 1000);
			}

			// Trigger Submit Facebook Pixel
			if (this.$store.getters.settings.facebookPixel) {
				try {
					window.fbq("track", "SubmitCompleted", {
						content_name: "Form Succesfully Submitted",
					});
				} catch (error) {
					console.log(error);
				}
			}

			// Custom text detection (Coca-Cola)
			// 1926 -> PBX Packageless
			// 2548 -> Capri-Sun 2022
			// 2695 -> CCEP Belgium
			// 3194 -> CCEP Belgium W44
			// 4414 -> CCEP Cora lux tombola
			if (
				this.$store.getters.pageId == 1926 ||
				this.$store.getters.pageId == 2548 ||
				this.$store.getters.pageId == 2695 ||
				this.$store.getters.pageId == 3194 ||
				this.$store.getters.pageId == 4414
			) {
				this.$refs.popupDialog.open();
			} else {
				if (resp.type == "redirect") {
					// Directly redirect to coupon/voucher
					window.location = resp.target;
				} else if (resp.type == "wallet") {
					this.target = resp.target;

					// Check if device is mobile or desktop
					if (isMobile) {
						window.location = resp.target;

						// Set a slight delay as the target still have to load
						setTimeout(() => {
							this.$refs.popupDialog.open();
							this.submitting = false;
						}, 2000);
					} else {
						this.$refs.popupDialog.setWalletUrl(resp.target);
						this.$refs.popupDialog.open();
					}
				} else {
					this.$refs.popupDialog.open();
				}
			}

			// Delay submission on mobile wallet
			if (!(resp.type == "wallet" && isMobile)) this.submitting = false;
		},
		invalid(resp) {
			if (resp.error_code == 1) this.$refs.duplicateDialog.open();
			else if (resp.error_code == 6) this.$refs.invalidCodeDialog.open();
			else if (resp.error_code == 8)
				this.$refs.missingRequiredDialog.open();
			else if (resp.error_code == 9)
				this.$refs.invalidFieldDataDialog.open();
			else if (resp.error_code == 10)
				this.$refs.requiredTermsAndConditionsDialog.open();
			else if (resp.error_code == 12)
				this.$refs.customer26692WrongAnswerErrorDialog.open(); //this is custom work for customer 26692
			else if (resp.error_code == 13)
				this.$refs.customer26692duplicateDialogDialog.open(); //this is custom work for customer 26692
			else this.$refs.errorDialog.open();

			this.submitting = false;
		},
		meetsRestrictionRequirements() {
			if (Object.keys(this.restrictions).length == 0) return true;

			let restrictionFailed = false;
			let inputs = this.dModule.data.form.input;
			for (const key of Object.keys(this.restrictions)) {
				var index = key.lastIndexOf("-");
				var inputId = parseInt(key.substring(index + 1));

				var input = inputs.find((obj) => {
					return obj.id === inputId;
				});

				var restrictMin = input.restrictMin;
				var restrictMax = input.restrictMax;

				this.restrictions[key] =
					!(
						restrictMin == null ||
						restrictMin <= this.formValues[key]
					) ||
					!(
						restrictMax == null ||
						restrictMax >= this.formValues[key]
					);

				if (this.restrictions[key]) restrictionFailed = true;
			}

			if (restrictionFailed) this.submitting = false;

			return !restrictionFailed;
		},
		isValidPhone() {
			this.phoneInputError = null;

			for (const value of Object.values(this.phoneObject)) {
				// the value is null if we delete our inputted number
				if (value !== null && !value.isValid)
					this.phoneInputError = this.$t("form.error.invalid-phone");
			}

			// Coca-Cola check
			let pageId = this.$store.getters.pageId;
			if (pageId == 1925 && !this.hasRegionCode("SE")) {
				this.phoneInputError =
					"För att delta i kampanjen behöver du ett svenskt telefonnummer";
			}

			this.submitting = this.phoneInputError ? false : true;

			return this.phoneInputError ? false : true;
		},
		hasRegionCode(regionCode) {
			for (const value of Object.values(this.phoneObject)) {
				if (value.regionCode == regionCode) return true;
			}

			return false;
		},
		prefillInputs() {
			const inputs = this.dModule.data.form.input;

			// Fillable input types
			// {key: url parameter, value: input type to fill}
			const inputTypeInputs = {
				email: "email", // Legacy support
				prefill_email: "email",
				// prefill_phone: "tel", // This has formatting issues so users would be able to fill in faulty data
			};
			for (const parameterName in inputTypeInputs) {
				const param = this.getParameterByName(parameterName);
				if (!param) continue;

				const inputType = inputTypeInputs[parameterName];
				inputs.forEach((input) => {
					if (input.inputType === inputType) {
						Vue.set(this.formValues, "input-" + input.id, param);
					}
				});
			}

			// Fillable field types
			// {key: url parameter, value: field type to fill}
			const fieldTypeInputs = {
				firstname: "firstname", // Legacy support
				lastname: "lastname", // Legacy support
				prefill_first_name: "firstname",
				prefill_last_name: "lastname",
				// prefill_phone: "phone", // This has formatting issues so users would be able to fill in faulty data
				prefill_city: "city",
				prefill_address: "address",
				prefill_zip: "zip",
				prefill_custom_1: "customfield1",
				prefill_custom_2: "customfield2",
				prefill_custom_3: "customfield3",
				prefill_custom_4: "customfield4",
				prefill_custom_5: "customfield5",
				prefill_custom_6: "customfield6",
				prefill_custom_7: "customfield7",
				prefill_custom_8: "customfield8",
			};
			for (const parameterName in fieldTypeInputs) {
				const param = this.getParameterByName(parameterName);
				if (!param) continue;

				const fieldType = fieldTypeInputs[parameterName];
				inputs.forEach((input) => {
					if (input.fieldType === fieldType) {
						Vue.set(this.formValues, "input-" + input.id, param);
					}
				});
			}
		},
		getParameterByName(name, url = window.location.href) {
			//eslint-disable-next-line
			name = name.replace(/[\[\]]/g, "\\$&");
			var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
			var results = regex.exec(url);
			if (!results) return null;
			if (!results[2]) return "";
			return decodeURIComponent(results[2].replace(/\+/g, " "));
		},
		openTerms(e) {
			e.stopPropagation();

			EventBus.$emit("open-terms");
		},
		openWallet() {
			if (isMobile && this.target != null) window.location = this.target;
		},
		toggleShow: function (itemId) {
			this.hidingItems["input-" + itemId] =
				!this.hidingItems["input-" + itemId];
		},
		onSuccessfulSumbitDialogClosed() {
			// Reload the page if the dialog is closed after submittion
			if (this.dModule.data.action.refreshtimerEnabled)
				window.location.reload();
		},
	},
	computed: {
		formStyle() {
			return {
				color: this.dModule.layout.labelColor,
			};
		},
		inputStyle() {
			let layout = this.dModule.layout;

			if (layout.inputStyle == "outlined") {
				return {
					border: "1px solid " + layout.inputColor,
					borderRadius: "4px !important",
					color: layout.inputColor,
				};
			} else {
				return {
					borderBottom: "1px solid " + layout.inputColor,
					borderTop: "none",
					borderLeft: "none",
					borderRight: "none",
					borderRadius: "0 !important",
					color: layout.inputColor,
				};
			}
		},
		buttonStyle() {
			let button = this.dModule.data.button;

			let borderRadius = 0;
			if (button.radius == "edged") borderRadius = 5;
			else if (button.radius == "rounded") borderRadius = 25;

			let width = "max-content";
			if (button.size == "half") width = "50%";
			else if (button.size == "full") width = "100%";

			return {
				color: button.textColor,
				backgroundColor: button.backgroundColor,
				borderColor: button.borderColor,
				borderRadius: borderRadius + "px",
				width: width,
			};
		},
		// TODO remove after customer action is fninished
		customer26692WrongAnswerText() {
			switch (this.$store.getters.settings.language) {
				case "de":
					return "Schade! Das war nicht die richtige Antwort. Versuchen Sie es morgen noch einmal!";
				case "nl":
					return "Helaas! Dat was niet het juiste antwoord. Probeer het morgen opnieuw!";
				default:
					return "Dommage! Ce n’était pas la bonne réponse. Retentez votre chance demain!";
			}
		},
		customer26692DuplicateText() {
			switch (this.$store.getters.settings.language) {
				case "de":
					return "Sie haben heute schon Ihr Glück versucht! Versuchen Sie es morgen noch einmal.";
				case "nl":
					return "Je hebt je geluk vandaag al geprobeerd! Probeer het morgen nog eens.";
				default:
					return "Vous avez déjà tenté votre chance aujourd’hui! Réessayez demain.";
			}
		},
	},
};
</script>

<style scoped>
.submit__btn {
	display: inline-block;
	padding: 5px 15px 5px 15px;
	text-align: center;
	border: 1px solid;
	border-radius: 5px;
}

.submit__btn:hover {
	cursor: pointer;
}

.error-text {
	color: #f44336 !important;
}

.error-text {
	font-size: 15px;
	font-weight: 500;
}

.input-error {
	border-color: #f44336 !important;
	border-width: 2px !important;
	margin-bottom: 0 !important;
}

label.required::after {
	font-weight: 300;
	content: "*";
}

.form-label {
	font-weight: bold;
	overflow: hidden;
	word-wrap: break-word;
}

.form-input__regular {
	padding-left: 10px;
	padding-right: 10px;
	padding-top: 5px;
	padding-bottom: 5px;
}

select {
	appearance: menulist !important;
	-webkit-appearance: menulist !important;
	min-height: 36px;
}

/* Define an animation behavior */
@keyframes spinner {
	to {
		transform: rotate(360deg);
	}
}
/* This is the class name given by the Font Awesome component when icon contains 'spinner' */
.spinner {
	/* Apply 'spinner' keyframes looping once every second (1s)  */
	animation: spinner 1s linear infinite;
}

.obfuscate-icon {
	position: absolute;
	margin-inline-start: -20px;
	margin-top: 10px;
}
</style>

<style>
.obfuscated {
	-webkit-text-security: disc;
}
.obfuscated .vti__input::placeholder,
.obfuscated .vti__dropdown {
	-webkit-text-security: none !important;
}

.form-input {
	width: 100%;
	-webkit-box-sizing: border-box;
	-moz-box-sizing: border-box;
	box-sizing: border-box;
}
</style>